"use client";

import React, { useState, useMemo, useCallback, useEffect } from "react";
import {
  ArrowLeft,
  Search,
  ChevronRight,
  Network,
  Building2,
  Server as ServerIcon,
  Eye,
  EyeOff,
  Loader2,
  AlertCircle,
  Plus,
  X,
} from "lucide-react";
import { Button } from "@/shadcn-components/ui/button";
import { Input } from "@/shadcn-components/ui/input";
import { Card, CardContent } from "@/shadcn-components/ui/card";
import { Badge } from "@/shadcn-components/ui/badge";
import { Label } from "@/shadcn-components/ui/label";
import { Switch } from "@/shadcn-components/ui/switch";
import { Alert, AlertDescription } from "@/shadcn-components/ui/alert";
import { Separator } from "@/shadcn-components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shadcn-components/ui/select";
import {
  useGatewaysStore,
  Gateway,
  GatewayCompany,
  GatewayServerNode,
  GatewayEndpoint,
  CustomGateway,
  CUSTOM_GATEWAY_SLUG_PREFIX,
  fetchGatewayCompanies,
  fetchGatewayMeta,
  useGatewayMeta,
  useGatewayAddressHealth,
  accountHome,
  allProbed,
  chooseBestAddress,
  hubScheme,
  uniqueEndpoints,
} from "@/lib/gateways";
import { GatewayAddressList } from "./GatewayAddressList";
import { GatewayAddressField } from "./GatewayAddressField";
import { useIsDeskPlan } from "@/lib/vybe-auth";
import { useAccountsStore } from "@/lib/accounts/store";
import { TradingAccountInput } from "@/lib/accounts/types";

interface AddAccountContentProps {
  onBack: () => void;
  onAccountAdded: () => void;
}

type AddAccountView =
  | "gateway-select"
  | "company-select"
  | "server-select"
  | "account-form";

const VIEW_ORDER: AddAccountView[] = [
  "gateway-select",
  "company-select",
  "server-select",
  "account-form",
];

/** Cap unvirtualized rows so huge catalogs (MT5 has thousands) stay smooth; search narrows further. */
const MAX_ROWS = 250;

/** Small broker/company avatar — renders the catalog logo, falls back to initials. */
function CatalogIcon({
  name,
  logoUrl,
  className = "h-10 w-10",
}: {
  name: string;
  logoUrl?: string | null;
  className?: string;
}) {
  const [broken, setBroken] = useState(false);
  const initials = (name || "?").replace(/[^A-Za-z0-9]/g, "").slice(0, 2).toUpperCase() || "?";
  if (!logoUrl || broken) {
    return (
      <div
        className={`${className} shrink-0 rounded-lg bg-muted/60 flex items-center justify-center text-xs font-semibold text-muted-foreground`}
      >
        {initials}
      </div>
    );
  }
  return (
    <div className={`${className} shrink-0 rounded-lg bg-muted/40 border border-border/60 flex items-center justify-center overflow-hidden`}>
      <img
        src={logoUrl}
        alt=""
        className="h-full w-full object-contain p-1"
        onError={() => setBroken(true)}
        loading="lazy"
      />
    </div>
  );
}

/**
 * A gateway's own icon on its card — reported by the gateway (independent of the company / server
 * logos inside it). Falls back to the generic gateway glyph when the gateway reports none or the
 * image fails to load.
 */
function GatewayIcon({ logoUrl }: { logoUrl?: string | null }) {
  const [broken, setBroken] = useState(false);
  if (!logoUrl || broken) {
    return (
      <div className="h-10 w-10 rounded-lg bg-muted/60 flex items-center justify-center shrink-0">
        <Network className="h-5 w-5 text-muted-foreground" />
      </div>
    );
  }
  return (
    <div className="h-10 w-10 shrink-0 rounded-lg bg-muted/40 border border-border/60 flex items-center justify-center overflow-hidden">
      <img
        src={logoUrl}
        alt=""
        className="h-full w-full object-contain p-1"
        onError={() => setBroken(true)}
        loading="lazy"
      />
    </div>
  );
}

function StepHeader({
  onBack,
  title,
  subtitle,
  icon,
}: {
  onBack: () => void;
  title: React.ReactNode;
  subtitle: React.ReactNode;
  icon?: React.ReactNode;
}) {
  return (
    <div className="px-5 py-4 border-b border-border">
      <div className="flex items-center gap-3 pr-8">
        <Button variant="ghost" size="icon" onClick={onBack} className="h-8 w-8 shrink-0">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        {icon}
        <div className="min-w-0 space-y-0.5">
          <h2 className="text-base font-semibold leading-tight truncate">{title}</h2>
          <p className="text-xs text-muted-foreground truncate">{subtitle}</p>
        </div>
      </div>
    </div>
  );
}

export function AddAccountContent({ onBack, onAccountAdded }: AddAccountContentProps) {
  const [currentView, setCurrentView] = useState<AddAccountView>("gateway-select");

  const [selectedGateway, setSelectedGateway] = useState<Gateway | null>(null);
  const [companies, setCompanies] = useState<GatewayCompany[]>([]);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [companiesError, setCompaniesError] = useState<string | null>(null);
  const [companyQuery, setCompanyQuery] = useState("");

  const [selectedCompany, setSelectedCompany] = useState<GatewayCompany | null>(null);
  const [serverQuery, setServerQuery] = useState("");

  const [selectedServer, setSelectedServer] = useState<GatewayServerNode | null>(null);
  const [selectedEndpointIdx, setSelectedEndpointIdx] = useState(0);
  /** True once the trader picked a gateway address themselves — the automatic choice then stands down. */
  const [addressTouched, setAddressTouched] = useState(false);

  const [login, setLogin] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [savePassword, setSavePassword] = useState(true);
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const gateways = useGatewaysStore((state) => state.gateways);
  const isLoadingGateways = useGatewaysStore((state) => state.isLoading);
  const customGateways = useGatewaysStore((state) => state.customGateways);
  const addCustomGateway = useGatewaysStore((state) => state.addCustom);
  const removeCustomGateway = useGatewaysStore((state) => state.removeCustom);
  const isDeskPlan = useIsDeskPlan();
  const addAccount = useAccountsStore((state) => state.addAccount);

  // ── Desk-only custom gateway form (localStorage only — never synced) ───────
  const [showCustomForm, setShowCustomForm] = useState(false);
  const [customName, setCustomName] = useState("");
  const [customHubUrl, setCustomHubUrl] = useState("");
  const [customHost, setCustomHost] = useState("");
  const [customPort, setCustomPort] = useState("");
  const [customFormError, setCustomFormError] = useState<string | null>(null);

  const goTo = (view: AddAccountView) => setCurrentView(view);

  // ── Several gateways behind one card ───────────────────────────────────────
  // A gateway whose /api/meta says `endpointsAreGateways` runs several instances; a server's
  // endpoints are then the hub addresses themselves and the trader picks one (the best reachable is
  // preselected). Absent/false — every MT5-style gateway, and custom ones — nothing below applies.
  const gatewayMeta = useGatewayMeta(
    selectedGateway && !selectedGateway.slug.startsWith(CUSTOM_GATEWAY_SLUG_PREFIX)
      ? selectedGateway.hubUrl
      : null
  );
  const addressesAreGateways = gatewayMeta.endpointsAreGateways;
  const addressScheme = hubScheme(selectedGateway?.hubUrl);
  const companyAddresses = useMemo(
    () => uniqueEndpoints((selectedCompany?.servers ?? []).flatMap((s) => s.endpoints)),
    [selectedCompany]
  );
  const { health: addressHealth } = useGatewayAddressHealth(
    companyAddresses,
    addressScheme,
    addressesAreGateways && selectedCompany !== null
  );
  /** The address to offer first for a server: the best reachable once all are probed, else the first. */
  const bestAddressIdx = useCallback(
    (server: GatewayServerNode) =>
      addressesAreGateways && allProbed(server.endpoints, addressHealth)
        ? chooseBestAddress(server.endpoints, addressHealth)
        : 0,
    [addressesAreGateways, addressHealth]
  );
  useEffect(() => {
    if (!addressesAreGateways || !selectedServer || addressTouched) return;
    setSelectedEndpointIdx(bestAddressIdx(selectedServer));
  }, [addressesAreGateways, selectedServer, addressTouched, bestAddressIdx]);

  const handleGatewaySelect = useCallback(async (gateway: Gateway) => {
    setSelectedGateway(gateway);
    void fetchGatewayMeta(gateway.hubUrl); // warm the address facts while the companies load
    setSelectedCompany(null);
    setSelectedServer(null);
    setCompanyQuery("");
    setCompaniesError(null);
    setCompanies([]);
    setCurrentView("company-select");
    setCompaniesLoading(true);
    try {
      const list = await fetchGatewayCompanies(gateway.hubUrl);
      setCompanies(list);
    } catch (err) {
      setCompaniesError(
        err instanceof Error ? err.message : "Failed to load companies"
      );
    } finally {
      setCompaniesLoading(false);
    }
  }, []);

  /**
   * Selecting a custom gateway: probe the hub for a browsable catalog with a
   * short timeout; if it answers, fall back to the normal company/server
   * browse — otherwise skip straight to the account form with the stored
   * host:port pre-filled.
   */
  const handleCustomGatewaySelect = useCallback(async (cg: CustomGateway) => {
    const gateway: Gateway = {
      slug: `${CUSTOM_GATEWAY_SLUG_PREFIX}${cg.id}`,
      name: cg.name,
      hubUrl: cg.hubUrl,
      accepting: true,
      servers: [{ name: cg.name, host: cg.host, port: cg.port }],
    };
    setSelectedGateway(gateway);
    setCompanyQuery("");
    setCompaniesError(null);
    setError(null);

    let companiesList: GatewayCompany[] = [];
    try {
      companiesList = await Promise.race([
        fetchGatewayCompanies(cg.hubUrl),
        new Promise<GatewayCompany[]>((_, reject) =>
          setTimeout(() => reject(new Error("probe timeout")), 2500)
        ),
      ]);
    } catch {
      companiesList = [];
    }

    if (companiesList.length > 0) {
      setCompanies(companiesList);
      setSelectedCompany(null);
      setSelectedServer(null);
      setCurrentView("company-select");
      return;
    }

    // Unreachable or empty catalog — pre-fill the stored endpoint directly.
    const server: GatewayServerNode = {
      name: cg.name,
      endpoints: [{ host: cg.host, port: cg.port }],
    };
    setSelectedCompany({ company: cg.name, servers: [server] });
    setSelectedServer(server);
    setSelectedEndpointIdx(0);
    setCurrentView("account-form");
  }, []);

  const handleAddCustomGateway = () => {
    setCustomFormError(null);
    const name = customName.trim();
    const hubUrl = customHubUrl.trim().replace(/\/+$/, "");
    const host = customHost.trim();
    const port = Number(customPort.trim());
    if (!name) return setCustomFormError("Enter a name");
    if (!/^https?:\/\//i.test(hubUrl))
      return setCustomFormError("Hub URL must start with http:// or https://");
    if (!host) return setCustomFormError("Enter the trade host");
    if (!Number.isInteger(port) || port < 1 || port > 65535)
      return setCustomFormError("Port must be 1–65535");
    addCustomGateway({ name, hubUrl, host, port });
    setCustomName("");
    setCustomHubUrl("");
    setCustomHost("");
    setCustomPort("");
    setShowCustomForm(false);
  };

  const handleCompanySelect = (company: GatewayCompany) => {
    setSelectedCompany(company);
    setSelectedServer(null);
    setServerQuery("");
    goTo("server-select");
  };

  const handleServerSelect = (server: GatewayServerNode) => {
    setSelectedServer(server);
    setSelectedEndpointIdx(0);
    setAddressTouched(false);
    setError(null);
    goTo("account-form");
  };

  /** The trader picked one gateway address of a server themselves. */
  const handleAddressSelect = (server: GatewayServerNode, endpointIdx: number) => {
    setSelectedServer(server);
    setSelectedEndpointIdx(endpointIdx);
    setAddressTouched(true);
    setError(null);
    goTo("account-form");
  };

  const filteredCompanies = useMemo(() => {
    const q = companyQuery.trim().toLowerCase();
    if (!q) return companies;
    return companies.filter(
      (c) =>
        c.company.toLowerCase().includes(q) ||
        c.servers.some(
          (s) =>
            s.name.toLowerCase().includes(q) ||
            s.endpoints.some((e) => e.host.toLowerCase().includes(q))
        )
    );
  }, [companies, companyQuery]);

  const filteredServers = useMemo(() => {
    const servers = selectedCompany?.servers ?? [];
    const q = serverQuery.trim().toLowerCase();
    if (!q) return servers;
    return servers.filter(
      (s) =>
        s.name.toLowerCase().includes(q) ||
        s.endpoints.some((e) => e.host.toLowerCase().includes(q))
    );
  }, [selectedCompany, serverQuery]);

  const selectedEndpoint: GatewayEndpoint | undefined =
    selectedServer?.endpoints[selectedEndpointIdx] ?? selectedServer?.endpoints[0];

  const isSignInEnabled = Boolean(
    selectedServer && selectedEndpoint && login.trim() && password.trim()
  );

  const handleSignIn = () => {
    setError(null);
    if (!selectedGateway || !selectedCompany || !selectedServer || !selectedEndpoint) {
      setError("Please select a gateway, company and server");
      return;
    }
    if (!login.trim()) return setError("Please enter your login");
    if (!password.trim()) return setError("Please enter your password");

    setIsSigningIn(true);
    try {
      // Gateway accounts don't pick a client-side provider — the gateway pins its
      // own default (Gateway__Protocol); the terminal omits X-Trading-Provider.
      const newAccount: TradingAccountInput = {
        companyName: selectedCompany.company,
        serverName: selectedServer.name,
        providerKind: "T5",
        credentials: {
          login: login.trim(),
          password: savePassword ? password : "",
        },
        createdAt: Date.now(),
        lastConnectedAt: Date.now(),
        // One gateway per card: hubUrl = the card's hub, host/port = the upstream endpoint. Several
        // gateways per card: the chosen endpoint IS the hub the account dials.
        ...accountHome(selectedGateway, selectedEndpoint, addressesAreGateways),
        brokerLogoUrl: selectedCompany.logoUrl ?? selectedServer.logoUrl ?? undefined,
      };
      addAccount(newAccount);
      onAccountAdded();
    } catch (err) {
      console.error("Error during sign in:", err);
      setError(err instanceof Error ? err.message : "Failed to add account");
    } finally {
      setIsSigningIn(false);
    }
  };

  // ── View 1: gateway selection ──────────────────────────────────────────────
  const renderGatewaySelect = () => (
    <div className="flex flex-col h-full">
      <StepHeader
        onBack={onBack}
        title="Add Trading Account"
        subtitle="Select a gateway to get started"
      />
      <div className="flex-1 overflow-y-auto px-5 py-4">
        {isLoadingGateways && gateways.length === 0 ? (
          <div className="flex items-center justify-center py-12 text-sm text-muted-foreground gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading gateways…
          </div>
        ) : gateways.length === 0 ? (
          <EmptyState
            icon={<Network className="h-7 w-7 text-muted-foreground/70" />}
            title="No gateways available"
            body="The gateway topology could not be loaded. Check that the CRM is reachable."
          />
        ) : (
          <div className="space-y-2">
            {gateways.map((gateway) => {
              const serverCount = gateway.servers.length;
              return (
                <button
                  key={gateway.slug}
                  onClick={() => handleGatewaySelect(gateway)}
                  className="w-full text-left rounded-xl border border-border bg-card hover:border-primary/50 hover:bg-accent/40 transition-colors p-3.5 flex items-center gap-3 group"
                >
                  <GatewayIcon logoUrl={gateway.logoUrl} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="font-medium text-sm truncate">{gateway.name}</span>
                    </div>
                    <div className="text-xs text-muted-foreground truncate mt-0.5">
                      {serverCount.toLocaleString()} server{serverCount !== 1 ? "s" : ""}
                      {" • "}
                      <span className={gateway.accepting ? "text-emerald-500" : "text-muted-foreground"}>
                        {gateway.accepting ? "accepting" : "not accepting"}
                      </span>
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5 text-muted-foreground shrink-0 group-hover:translate-x-0.5 transition-transform" />
                </button>
              );
            })}
          </div>
        )}

        {/* Desk-only custom gateways — stored on this device only, never synced */}
        {isDeskPlan && (
          <div className="mt-6">
            <div className="flex items-center gap-3 mb-2 px-1">
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium whitespace-nowrap">
                My gateways · stored on this device only
              </span>
              <Separator className="flex-1" />
            </div>
            <div className="space-y-1.5">
              {customGateways.map((cg) => (
                <div
                  key={cg.id}
                  className="w-full rounded-xl border border-border bg-card hover:border-primary/50 hover:bg-accent/40 transition-colors p-3 flex items-center gap-3 group"
                >
                  <button
                    onClick={() => void handleCustomGatewaySelect(cg)}
                    className="flex-1 min-w-0 text-left flex items-center gap-3"
                  >
                    <div className="h-9 w-9 rounded-lg bg-muted/50 flex items-center justify-center shrink-0">
                      <ServerIcon className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="font-medium text-sm truncate">{cg.name}</span>
                        <Badge variant="secondary" className="text-[10px] px-1.5 py-0 shrink-0">
                          LOCAL
                        </Badge>
                      </div>
                      <div className="text-xs text-muted-foreground truncate font-mono mt-0.5">
                        {cg.hubUrl} · {cg.host}:{cg.port}
                      </div>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => removeCustomGateway(cg.id)}
                    className="text-muted-foreground hover:text-destructive shrink-0 p-1"
                    aria-label={`Remove ${cg.name}`}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}

              {showCustomForm ? (
                <div className="rounded-xl border border-border bg-card p-3 space-y-2.5">
                  {customFormError && (
                    <p className="text-xs text-destructive">{customFormError}</p>
                  )}
                  <Input
                    placeholder="Name"
                    value={customName}
                    onChange={(e) => setCustomName(e.target.value)}
                    className="h-8 text-sm"
                  />
                  <Input
                    placeholder="Hub URL (http://localhost:5031)"
                    value={customHubUrl}
                    onChange={(e) => setCustomHubUrl(e.target.value)}
                    className="h-8 text-sm font-mono"
                  />
                  <div className="flex gap-2">
                    <Input
                      placeholder="Trade host"
                      value={customHost}
                      onChange={(e) => setCustomHost(e.target.value)}
                      className="h-8 text-sm font-mono flex-1"
                    />
                    <Input
                      placeholder="Port"
                      inputMode="numeric"
                      value={customPort}
                      onChange={(e) => setCustomPort(e.target.value)}
                      className="h-8 text-sm font-mono w-24"
                    />
                  </div>
                  <div className="flex gap-2 justify-end pt-0.5">
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 text-xs"
                      onClick={() => {
                        setShowCustomForm(false);
                        setCustomFormError(null);
                      }}
                    >
                      Cancel
                    </Button>
                    <Button size="sm" className="h-7 text-xs" onClick={handleAddCustomGateway}>
                      Add gateway
                    </Button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setShowCustomForm(true)}
                  className="w-full text-left rounded-xl border border-dashed border-border hover:border-primary/50 hover:bg-accent/40 transition-colors p-3 flex items-center gap-3 text-muted-foreground hover:text-foreground"
                >
                  <div className="h-9 w-9 rounded-lg bg-muted/30 flex items-center justify-center shrink-0">
                    <Plus className="h-4 w-4" />
                  </div>
                  <span className="text-sm">Add custom gateway</span>
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // ── View 2: company selection ──────────────────────────────────────────────
  const renderCompanySelect = () => (
    <div className="flex flex-col h-full">
      <StepHeader
        onBack={() => goTo("gateway-select")}
        title={selectedGateway?.name ?? "Companies"}
        subtitle="Select a company"
      />
      <div className="px-5 pt-3">
        <SearchBox
          value={companyQuery}
          onChange={setCompanyQuery}
          placeholder="Search company, server or host…"
        />
      </div>
      <div className="flex-1 overflow-y-auto px-5 py-3">
        {companiesLoading ? (
          <div className="flex items-center justify-center py-12 text-sm text-muted-foreground gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading companies…
          </div>
        ) : companiesError ? (
          <EmptyState
            icon={<AlertCircle className="h-7 w-7 text-destructive/70" />}
            title="Couldn’t load companies"
            body={companiesError}
            action={
              <Button size="sm" variant="outline" onClick={() => selectedGateway && handleGatewaySelect(selectedGateway)}>
                Retry
              </Button>
            }
          />
        ) : filteredCompanies.length === 0 ? (
          <EmptyState
            icon={<Building2 className="h-7 w-7 text-muted-foreground/70" />}
            title="No companies found"
            body="Try a different search."
          />
        ) : (
          <div className="space-y-1.5">
            {filteredCompanies.length > MAX_ROWS && (
              <p className="text-[11px] text-muted-foreground px-1 pb-1">
                Showing {MAX_ROWS.toLocaleString()} of {filteredCompanies.length.toLocaleString()} — search to narrow.
              </p>
            )}
            {filteredCompanies.slice(0, MAX_ROWS).map((company) => {
              const count = company.servers.length;
              return (
                <button
                  key={company.company}
                  onClick={() => handleCompanySelect(company)}
                  className="w-full text-left rounded-xl border border-border bg-card hover:border-primary/50 hover:bg-accent/40 transition-colors p-3 flex items-center gap-3 group"
                >
                  <CatalogIcon name={company.company} logoUrl={company.logoUrl} className="h-9 w-9" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate">{company.company}</div>
                    <div className="text-xs text-muted-foreground truncate mt-0.5">
                      {count} server{count !== 1 ? "s" : ""}
                      {company.site ? ` • ${company.site}` : ""}
                    </div>
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0 group-hover:translate-x-0.5 transition-transform" />
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );

  // ── View 3: server selection ───────────────────────────────────────────────
  const renderServerSelect = () => (
    <div className="flex flex-col h-full">
      <StepHeader
        onBack={() => goTo("company-select")}
        title={selectedCompany?.company ?? "Servers"}
        subtitle="Select a broker server"
        icon={<CatalogIcon name={selectedCompany?.company ?? "?"} logoUrl={selectedCompany?.logoUrl} className="h-8 w-8" />}
      />
      <div className="px-5 pt-3">
        <SearchBox value={serverQuery} onChange={setServerQuery} placeholder="Search servers…" />
      </div>
      <div className="flex-1 overflow-y-auto px-5 py-3">
        {filteredServers.length === 0 ? (
          <EmptyState
            icon={<ServerIcon className="h-7 w-7 text-muted-foreground/70" />}
            title="No servers found"
            body="Try adjusting your search."
          />
        ) : (
          <div className="space-y-1.5">
            {filteredServers.length > MAX_ROWS && (
              <p className="text-[11px] text-muted-foreground px-1 pb-1">
                Showing {MAX_ROWS.toLocaleString()} of {filteredServers.length.toLocaleString()} — search to narrow.
              </p>
            )}
            {filteredServers.slice(0, MAX_ROWS).map((server) => {
              const ep = server.endpoints[0];
              const epCount = server.endpoints.length;
              if (addressesAreGateways) {
                // Each endpoint is a gateway address of this server — its own row, with live state.
                return (
                  <div key={server.name} className="space-y-1.5">
                    <div className="flex items-center gap-3 px-1 pt-1">
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm truncate">{server.name}</div>
                        <div className="text-xs text-muted-foreground truncate mt-0.5">
                          {epCount} gateway address{epCount !== 1 ? "es" : ""}
                        </div>
                      </div>
                    </div>
                    <GatewayAddressList
                      endpoints={server.endpoints}
                      health={addressHealth}
                      selectedIndex={bestAddressIdx(server)}
                      onSelect={(i) => handleAddressSelect(server, i)}
                    />
                  </div>
                );
              }
              return (
                <button
                  key={server.name}
                  onClick={() => handleServerSelect(server)}
                  className="w-full text-left rounded-xl border border-border bg-card hover:border-primary/50 hover:bg-accent/40 transition-colors p-3 flex items-center gap-3 group"
                >
                  <div className="h-9 w-9 rounded-lg bg-muted/50 flex items-center justify-center shrink-0">
                    <ServerIcon className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate">{server.name}</div>
                    <div className="text-xs text-muted-foreground truncate font-mono mt-0.5">
                      {ep ? `${ep.host}:${ep.port}` : "no endpoint"}
                      {epCount > 1 ? (
                        <span className="ml-1.5 font-sans text-[10px] text-primary">+{epCount - 1} more</span>
                      ) : null}
                    </div>
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0 group-hover:translate-x-0.5 transition-transform" />
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );

  // ── View 4: account form ───────────────────────────────────────────────────
  const renderAccountForm = () => (
    <div className="flex flex-col h-full">
      <StepHeader
        onBack={() => goTo("server-select")}
        title={selectedServer?.name ?? "Account"}
        subtitle={selectedCompany?.company ?? ""}
        icon={<CatalogIcon name={selectedCompany?.company ?? "?"} logoUrl={selectedCompany?.logoUrl} className="h-8 w-8" />}
      />
      <div className="flex-1 overflow-y-auto px-5 py-4">
        <div className="space-y-5">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Several gateways behind this server: the address is a row of the credentials card below */}
          {addressesAreGateways && selectedServer ? null : (
          /* Endpoint (host:port) selector */
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground font-medium">
              Server endpoint
            </Label>
            {selectedServer && selectedServer.endpoints.length > 1 ? (
              <Select
                value={String(selectedEndpointIdx)}
                onValueChange={(v) => setSelectedEndpointIdx(Number(v))}
              >
                <SelectTrigger className="w-full font-mono text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {selectedServer.endpoints.map((e, i) => (
                    <SelectItem key={`${e.host}:${e.port}`} value={String(i)} className="font-mono text-sm">
                      {e.host}:{e.port}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <div className="rounded-md border border-border bg-muted/30 px-3 py-2 font-mono text-sm text-foreground">
                {selectedEndpoint ? `${selectedEndpoint.host}:${selectedEndpoint.port}` : "—"}
              </div>
            )}
          </div>
          )}

          <div>
            <h3 className="text-muted-foreground text-xs uppercase font-medium tracking-wider mb-2.5">
              Account credentials
            </h3>
            <Card>
              <CardContent className="p-0">
                {addressesAreGateways && selectedServer ? (
                  <>
                    <GatewayAddressField
                      endpoints={selectedServer.endpoints}
                      health={addressHealth}
                      selectedIndex={selectedEndpointIdx}
                      onSelect={(i) => {
                        setSelectedEndpointIdx(i);
                        setAddressTouched(true);
                      }}
                    />
                    <Separator />
                  </>
                ) : null}
                <div className="flex items-center justify-between p-3.5">
                  <Label htmlFor="login" className="text-sm font-medium">Login</Label>
                  <Input
                    id="login"
                    type="text"
                    inputMode="numeric"
                    placeholder="enter login"
                    value={login}
                    onChange={(e) => setLogin(e.target.value)}
                    className="w-44 text-right border-none bg-transparent p-0 focus-visible:ring-0"
                  />
                </div>
                <Separator />
                <div className="flex items-center justify-between p-3.5 gap-2">
                  <Label htmlFor="password" className="text-sm font-medium">Password</Label>
                  <div className="flex items-center gap-1.5 w-44 justify-end">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="enter password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="text-right border-none bg-transparent p-0 focus-visible:ring-0"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      className="text-muted-foreground hover:text-foreground shrink-0"
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <Separator />
                <div className="flex items-center justify-between p-3.5">
                  <div className="space-y-0.5">
                    <Label htmlFor="save-password" className="text-sm font-medium">Save password</Label>
                    <p className="text-[11px] text-muted-foreground">Stored locally on this device only.</p>
                  </div>
                  <Switch id="save-password" checked={savePassword} onCheckedChange={setSavePassword} />
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      <div className="px-5 py-4 border-t border-border">
        <Button
          onClick={handleSignIn}
          disabled={!isSignInEnabled || isSigningIn}
          className="w-full h-11 text-sm font-medium"
          size="lg"
        >
          {isSigningIn ? (
            <span className="flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Signing In…</span>
          ) : (
            "Sign In"
          )}
        </Button>
      </div>
    </div>
  );

  const panels: Record<AddAccountView, React.ReactNode> = {
    "gateway-select": renderGatewaySelect(),
    "company-select": renderCompanySelect(),
    "server-select": renderServerSelect(),
    "account-form": renderAccountForm(),
  };
  const currentIdx = VIEW_ORDER.indexOf(currentView);

  return (
    <div className="h-full relative overflow-hidden">
      {VIEW_ORDER.map((view, idx) => (
        <div
          key={view}
          className="absolute inset-0 transition-transform duration-300 ease-in-out"
          style={{ transform: `translateX(${(idx - currentIdx) * 100}%)` }}
          aria-hidden={view !== currentView}
        >
          {panels[view]}
        </div>
      ))}
    </div>
  );
}

function SearchBox({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
}) {
  return (
    <div className="relative">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
      <Input
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="pl-9"
      />
    </div>
  );
}

function EmptyState({
  icon,
  title,
  body,
  action,
}: {
  icon: React.ReactNode;
  title: string;
  body: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <div className="w-14 h-14 rounded-full bg-muted/50 flex items-center justify-center mb-3">
        {icon}
      </div>
      <h3 className="text-sm font-medium mb-1.5">{title}</h3>
      <p className="text-muted-foreground text-xs max-w-xs">{body}</p>
      {action ? <div className="mt-4">{action}</div> : null}
    </div>
  );
}
